"use client";

import type { DefaultNodeTypes, SerializedUploadNode } from "@payloadcms/richtext-lexical";
import type { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import type { Media } from "@portal/payload-types";
import { type JSXConvertersFunction, RichText } from "@payloadcms/richtext-lexical/react";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { kebabCase } from "@portal/app/_common/lib/changeCase";
import { articleImageStyles } from "@portal/app/_components/ui-elements/articleImageStyles";
import {
  tableScrollContainerStyles,
  tableStyles,
} from "@portal/app/_components/ui-elements/articleTableStyles";
import { BLOG_IMAGE_SIZES, DEFAULT_BLOG_IMAGE_SIZE, type BlogImageSize } from "../../blogImageSize";
import { BLOG_INLINE_IMAGE_SIZES, getBlogImageProps } from "../../blogImage";
import type { BlogRichText } from "../../payloadRichText";

/**
 * Upload nodes carry the author's size choice on `fields.size`. Anything else —
 * a node saved before the field existed (`fields: null`), or a value no longer
 * in the option list — renders at the default rather than dropping the image.
 */
export function resolveBlogImageSize(node: SerializedUploadNode): BlogImageSize {
  const size = (node.fields as { size?: unknown } | null | undefined)?.size;
  return BLOG_IMAGE_SIZES.find((candidate) => candidate === size) ?? DEFAULT_BLOG_IMAGE_SIZE;
}

/**
 * Replaces Payload's default upload converter, which emits a bare <img> with no
 * class, so we can tag each image with its size and let the stylesheet below
 * cap the width per breakpoint. Keeps the intrinsic width/height attributes so
 * the browser reserves the right aspect ratio before the file loads.
 *
 * Stays a plain <img> because eslint bans next/image here (it leaks memory in
 * the Next server). Article images sit below the fold, so they load lazily.
 */
const BlogUploadImage = ({ node }: { node: SerializedUploadNode }) => {
  // `value` is a bare ID string when the media doc was not populated, and is
  // typed as a union across every collection, so narrow on relationTo first.
  if (node.relationTo !== "media" || typeof node.value !== "object" || !node.value) {
    return null;
  }

  const doc = node.value as Media;
  if (!doc.url) {
    return null;
  }

  const size = resolveBlogImageSize(node);

  return (
    <img
      alt={doc.alt ?? ""}
      {...getBlogImageProps(doc.url, {
        sizes: BLOG_INLINE_IMAGE_SIZES[size],
        intrinsicWidth: doc.width,
      })}
      width={doc.width ?? undefined}
      height={doc.height ?? undefined}
      loading="lazy"
      decoding="async"
      className={`blog-image blog-image--${size}`}
    />
  );
};

const blogJsxConverters: JSXConvertersFunction<DefaultNodeTypes> = ({ defaultConverters }) => ({
  ...defaultConverters,
  upload: ({ node }) => <BlogUploadImage node={node} />,
});

export const BlogRichTextReader = ({
  content,
  "data-testid": dataTestid,
}: {
  content: BlogRichText;
  "data-testid"?: string;
}) => (
  <Box sx={{ width: "100%" }} {...(dataTestid && { "data-testid": kebabCase(dataTestid) })}>
    <Styles>
      <RichText converters={blogJsxConverters} data={content as SerializedEditorState} />
    </Styles>
  </Box>
);

const Styles = styled("div")(
  ({ theme }) => `
  width: 100%;
  font-size: 1em;
  line-height: 150%;
  position: relative;
  outline: 0;
  color: ${theme.palette.text.primary};

  strong,
  .editor-text-bold {
    font-weight: bold;
  }

  em,
  .editor-text-italic {
    font-style: italic;
  }

  u,
  .editor-text-underline {
    text-decoration: underline;
  }

  s,
  .editor-text-strikethrough {
    text-decoration: line-through;
  }

  code,
  .editor-text-code {
    background-color: rgb(240, 242, 245);
    padding: 1px 0.25rem;
    font-family: Menlo, Consolas, Monaco, monospace;
  }

  p,
  .editor-paragraph {
    margin: 0;
    margin-bottom: 1em;
    position: relative;
  }

  p:last-child,
  .editor-paragraph:last-child {
    margin-bottom: 0;
  }

  h1,
  .editor-heading-h1 {
    font-size: 2em;
    line-height: 140%;
    color: ${theme.palette.text.primary};
    font-weight: 400;
    margin: 0 0 12px;
    padding: 0;
  }

  h2,
  .editor-heading-h2 {
    font-size: 1.5em;
    line-height: 140%;
    color: ${theme.palette.text.primary};
    font-weight: 700;
    margin: 0 0 12px;
    padding: 0;
  }

  h3,
  .editor-heading-h3 {
    font-size: 1.2em;
    line-height: 140%;
    color: ${theme.palette.text.primary};
    font-weight: 700;
    margin: 0 0 12px;
    padding: 0;
  }

  ol,
  .editor-list-ol {
    padding: 0;
    margin: 0;
    margin-left: 16px;
    /* Explicit type: the global "ol, ul { list-style: inherit }" rule in
       GlobalStyle.tsx otherwise leaks in and makes ordered lists render as
       discs instead of numbers. Applies at every depth (the converter has no
       depth-specific classes), so nested ordered lists stay numbered too. */
    list-style-type: decimal;
  }

  ul,
  .editor-list-ul {
    padding: 0;
    margin: 0;
    margin-left: 16px;
    list-style-type: disc;
  }

  li,
  .editor-listitem {
    margin: 8px 32px 8px 32px;
  }

  /* Structural wrappers that only hold a sub-list are emitted by Payload's
     React converter with an inline "list-style-type: none", so they stay
     markerless on their own. We intentionally do NOT suppress markers on
     nested list items themselves — nested bullets/numbers should be visible. */

  /* Depth-cycling ordered markers, matching the admin Lexical editor exactly.
     The editor assigns ol1..ol5 by total list-nesting depth ((depth - 1) % 5):
     decimal → upper-alpha → lower-alpha → upper-roman → lower-roman. The React
     converter that renders this page does not replicate that (every ordered
     list is just "list-number"), so we recreate it. Each ":is(ul, ol)" chain
     counts one level of list nesting — counting ul and ol levels alike, as
     Lexical does — and a longer chain has higher specificity, so the deepest
     matching rule wins. Depths beyond 5 are left at lower-roman rather than
     cycling back; real blog content never nests lists that deeply.

     The leading "&" is required: these selectors start with a pseudo-class,
     and without it stylis attaches ":is(...)" to the component root (compiling
     to ".root:is(ul, ol)") instead of treating it as a descendant. The "& "
     forces the descendant combinator (".root :is(ul, ol) ol"). */
  & :is(ul, ol) ol {
    list-style-type: upper-alpha;
  }

  & :is(ul, ol) :is(ul, ol) ol {
    list-style-type: lower-alpha;
  }

  & :is(ul, ol) :is(ul, ol) :is(ul, ol) ol {
    list-style-type: upper-roman;
  }

  & :is(ul, ol) :is(ul, ol) :is(ul, ol) :is(ul, ol) ol {
    list-style-type: lower-roman;
  }

  ${articleImageStyles}

  /* Author-selected sizes. max-width rather than width so a small source image
     is never upscaled past its intrinsic size and blurred. */
  img.blog-image--small {
    max-width: 320px;
  }

  img.blog-image--medium {
    max-width: 520px;
  }

  img.blog-image--large {
    max-width: 100%;
  }

  /* Below md the column is only ~343px, where a 40%-of-column "small" would be
     too small to read a table or infographic. Widen both steps toward full
     width so the author's relative ordering survives without losing legibility. */
  ${theme.breakpoints.down("md")} {
    img.blog-image--small {
      max-width: 60%;
    }

    img.blog-image--medium {
      max-width: 85%;
    }
  }

  /* The wrapper is Payload's, not ours: its JSX table converter emits
     <div class="lexical-table-container">. It keeps scrolling the tables too
     wide to wrap. */
  .lexical-table-container {
    ${tableScrollContainerStyles(theme)}
  }

  /* Hardcoded rather than a prop: this reader only ever renders blog articles,
     and the blog is the surface that asked for full-width tables. Everywhere
     else goes through DreamMarkdownReader, which defaults to "scroll". */
  ${tableStyles(theme, "fit")}
`
);
